import React, { useEffect, useState } from 'react';
import { ChevronDown, ChevronRight, Plus, X } from 'lucide-react';
import { useSectionAccess } from '../../hooks/useSectionAccess';
import {
  CHILE_MOBILE_PLACEHOLDER,
  PHONE_MAX_LENGTH,
  chileanMobileError,
  formatChileanMobile,
  isChileanPhoneField,
} from '../../utils/textUtils';
import { SearchableSelect } from './SearchableSelect';

const FormDrawerSection = ({ section, compact, colors, children }) => {
  const [open, setOpen] = useState(() => section.defaultOpen !== false);

  useEffect(() => {
    setOpen(section.defaultOpen !== false);
  }, [section.key, section.defaultOpen]);

  const boxStyle = {
    marginBottom: compact ? '8px' : '12px',
    background: colors.sectionBg,
    border: `1px solid ${colors.sectionBorder}`,
    borderRadius: '8px',
    padding: compact ? '8px' : '10px',
  };

  if (section.flat) {
    return (
      <div style={boxStyle}>
        {section.title ? (
          <div style={{ color: colors.text, fontWeight: 600, fontSize: compact ? '12px' : '13px' }}>
            {section.title}
          </div>
        ) : null}
        {children}
      </div>
    );
  }

  const Chevron = open ? ChevronDown : ChevronRight;
  return (
    <div style={boxStyle}>
      <button
        type="button"
        onClick={() => setOpen((current) => !current)}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '6px',
          width: '100%',
          margin: 0,
          padding: 0,
          border: 'none',
          background: 'transparent',
          cursor: 'pointer',
          color: colors.text,
          fontWeight: 600,
          fontSize: compact ? '12px' : '13px',
          textAlign: 'left',
        }}
        aria-expanded={open}
      >
        <Chevron size={14} />
        {section.title}
      </button>
      {open ? children : null}
    </div>
  );
};

export const FormDrawer = ({
  isOpen,
  onClose,
  onSave,
  isSaving = false,
  isDarkMode = false,
  title = 'Formulario',
  subtitle = '',
  sections = [],
  formData = {},
  onChange,
  formError = '',
  width = 560,
  zIndex = 10000,
  readOnly = false,
  saveLabel = 'Guardar',
  footerActions = null,
  compact = false,
}) => {
  const access = useSectionAccess();
  const locked = !access.canCreate && !access.canUpdate;
  const effectiveReadOnly = readOnly || locked;
  const [phoneError, setPhoneError] = useState('');

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
      setPhoneError('');
      (sections || []).forEach((section) => {
        (section.fields || []).forEach((field) => {
          if (field.type === 'custom' || !isChileanPhoneField(field.key, field.type, field.label)) return;
          const current = formData?.[field.key];
          if (!current) return;
          const formatted = formatChileanMobile(current);
          if (formatted && formatted !== current) onChange?.(field.key, formatted);
        });
      });
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
    // Format existing phone values once when the drawer opens.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  if (!isOpen) return null;

  const colors = isDarkMode
    ? {
        overlay: 'rgba(0,0,0,0.55)',
        panel: '#18181b',
        panelBorder: '#2f2f35',
        title: '#f4f4f5',
        text: '#d4d4d8',
        muted: '#a1a1aa',
        inputBg: '#111113',
        inputBorder: '#3f3f46',
        inputText: '#e4e4e7',
        sectionBg: '#141417',
        sectionBorder: '#2a2a31',
        danger: '#f87171',
      }
    : {
        overlay: 'rgba(15,23,42,0.35)',
        panel: '#ffffff',
        panelBorder: '#e5e7eb',
        title: '#111827',
        text: '#1f2937',
        muted: '#6b7280',
        inputBg: '#ffffff',
        inputBorder: '#d1d5db',
        inputText: '#111827',
        sectionBg: '#f9fafb',
        sectionBorder: '#e5e7eb',
        danger: '#dc2626',
      };

  const baseInputStyle = {
    width: '100%',
    marginTop: compact ? '4px' : '6px',
    height: compact ? '32px' : '36px',
    boxSizing: 'border-box',
    borderRadius: '8px',
    border: `1px solid ${colors.inputBorder}`,
    backgroundColor: colors.inputBg,
    color: colors.inputText,
    padding: compact ? '0 8px' : '0 10px',
    fontSize: compact ? '12px' : '13px',
    outline: 'none',
  };

  const labelStyle = {
    color: colors.text,
    fontSize: compact ? '11px' : '12px',
    display: 'flex',
    flexDirection: 'column',
    minWidth: 0,
  };

  const shouldFormatPhone = (field) =>
    field?.type !== 'custom' && isChileanPhoneField(field.key, field.type, field.label);

  const emitChange = (field, nextValue) => {
    const formatted = shouldFormatPhone(field) ? formatChileanMobile(nextValue) : nextValue;
    if (phoneError && shouldFormatPhone(field) && !chileanMobileError(formatted)) {
      setPhoneError('');
    }
    onChange?.(field.key, formatted);
  };

  const validatePhones = () => {
    for (const section of sections || []) {
      for (const field of section.fields || []) {
        if (!shouldFormatPhone(field)) continue;
        const error = chileanMobileError(formData[field.key]);
        if (error) {
          setPhoneError(error);
          return false;
        }
      }
    }
    setPhoneError('');
    return true;
  };

  const handleSaveClick = () => {
    if (!validatePhones()) return;
    onSave?.();
  };

  const renderField = (field) => {
    const isPhone = shouldFormatPhone(field);
    const rawValue = formData[field.key] ?? '';
    const value = isPhone ? formatChileanMobile(rawValue) : rawValue;
    const fieldStyle = {
      ...labelStyle,
      ...(field.fullWidth
        ? { gridColumn: '1 / -1' }
        : Number(field.colSpan) > 1
          ? { gridColumn: `span ${Number(field.colSpan)}` }
          : null),
    };
    if (field.type === 'custom' && typeof field.render === 'function') {
      return (
        <div key={field.key} style={fieldStyle}>
          {field.render({
            value,
            onChange: (nextValue) => emitChange(field, nextValue),
            formData,
            isDarkMode,
            colors,
            baseInputStyle,
            compact,
          })}
        </div>
      );
    }
    const inputCommonProps = {
      style: baseInputStyle,
      value,
      disabled: effectiveReadOnly || field.disabled || false,
      readOnly: effectiveReadOnly || field.readOnly || false,
      onChange: (event) => emitChange(field, event.target.value),
    };

    return (
      <label key={field.key} style={fieldStyle}>
        {field.label}
        {field.type === 'select' ? (
          <select {...inputCommonProps}>
            {(field.options || []).map((option) => (
              <option key={String(option.value)} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        ) : field.type === 'searchable-select' ? (
          <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <SearchableSelect
                value={value}
                options={field.options || []}
                onChange={(nextValue) => emitChange(field, nextValue)}
                placeholder={field.placeholder || 'Escribe para buscar…'}
                allowFreeText={Boolean(field.allowFreeText)}
                disabled={effectiveReadOnly || field.disabled || false}
                style={baseInputStyle}
                isDarkMode={isDarkMode}
              />
            </div>
            {typeof field.onAdd === 'function' ? (
              <button
                type="button"
                onClick={field.onAdd}
                title={field.addButtonTitle || 'Agregar'}
                aria-label={field.addButtonTitle || 'Agregar'}
                disabled={effectiveReadOnly || field.disabled || false}
                style={{
                  flexShrink: 0,
                  width: compact ? '32px' : '34px',
                  height: compact ? '32px' : '34px',
                  borderRadius: '8px',
                  border: `1px solid ${colors.inputBorder}`,
                  background: colors.inputBg,
                  color: colors.text,
                  cursor: effectiveReadOnly || field.disabled ? 'not-allowed' : 'pointer',
                  opacity: effectiveReadOnly || field.disabled ? 0.5 : 1,
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  padding: 0,
                }}
              >
                <Plus size={16} />
              </button>
            ) : null}
          </div>
        ) : field.type === 'textarea' ? (
          <textarea
            {...inputCommonProps}
            rows={field.rows || (compact ? 2 : 4)}
            placeholder={field.placeholder || ''}
            style={{
              ...baseInputStyle,
              minHeight: `${Math.max(compact ? 56 : 90, (field.rows || (compact ? 2 : 4)) * 22)}px`,
              height: 'auto',
              padding: compact ? '6px 8px' : '8px 10px',
              resize: 'vertical',
            }}
          />
        ) : (
          <input
            {...inputCommonProps}
            type={isPhone ? 'tel' : field.type || 'text'}
            inputMode={isPhone ? 'tel' : undefined}
            autoComplete={isPhone ? 'tel' : undefined}
            maxLength={isPhone ? PHONE_MAX_LENGTH : undefined}
            placeholder={field.placeholder || (isPhone ? CHILE_MOBILE_PLACEHOLDER : '')}
            step={field.step}
          />
        )}
      </label>
    );
  };

  const renderSectionBody = (section) => {
    const fieldCount = (section.fields || []).length || 1;
    const columns =
      section.columns === 'auto'
        ? fieldCount
        : Number(section.columns || (compact ? fieldCount : 2));
    return (
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: `repeat(${Math.max(1, columns)}, minmax(0, 1fr))`,
          gap: compact ? '8px' : '10px',
          marginTop: section.flat || !section.title ? 0 : compact ? '6px' : '10px',
        }}
      >
        {(section.fields || []).map(renderField)}
      </div>
    );
  };

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        backgroundColor: colors.overlay,
        zIndex,
        display: 'flex',
        justifyContent: 'flex-end',
      }}
    >
      <div
        style={{
          width: `${width}px`,
          maxWidth: '98vw',
          height: '100vh',
          backgroundColor: colors.panel,
          borderLeft: `1px solid ${colors.panelBorder}`,
          display: 'flex',
          flexDirection: 'column',
        }}
        onClick={(event) => event.stopPropagation()}
      >
        <div
          style={{
            padding: compact ? '12px 14px' : '16px 18px',
            borderBottom: `1px solid ${colors.panelBorder}`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <div>
            <h3 style={{ margin: 0, color: colors.title, fontSize: compact ? '15px' : '16px' }}>{title}</h3>
            {subtitle ? (
              <p style={{ margin: '2px 0 0', color: colors.muted, fontSize: '12px' }}>{subtitle}</p>
            ) : null}
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={isSaving}
            style={{ background: 'transparent', border: 'none', color: colors.muted, cursor: 'pointer' }}
            aria-label="Cerrar"
          >
            <X size={18} />
          </button>
        </div>

        <div style={{ flex: 1, overflowY: 'auto', padding: compact ? '10px 14px' : '14px 18px' }}>
          {sections.map((section) => (
            <FormDrawerSection
              key={`${section.key}-${section.defaultOpen === false ? 'closed' : 'open'}`}
              section={section}
              compact={compact}
              colors={colors}
            >
              {renderSectionBody(section)}
            </FormDrawerSection>
          ))}

          {formError || phoneError ? (
            <p style={{ color: colors.danger, margin: '8px 2px 0', fontSize: '12px' }}>
              {formError || phoneError}
            </p>
          ) : null}
        </div>

        <div
          style={{
            borderTop: `1px solid ${colors.panelBorder}`,
            padding: compact ? '10px 14px' : '12px 18px',
            display: 'flex',
            justifyContent: 'flex-end',
            gap: '8px',
          }}
        >
          {Array.isArray(footerActions) && footerActions.length ? (
            footerActions.map((action) => {
              const variant = action.variant || 'secondary';
              const isPrimary = variant === 'primary';
              const isSuccess = variant === 'success';
              return (
                <button
                  key={action.key || action.label}
                  type="button"
                  onClick={action.onClick}
                  disabled={isSaving || action.disabled}
                  style={{
                    height: '34px',
                    borderRadius: '8px',
                    padding: '0 12px',
                    border: isPrimary || isSuccess ? 'none' : `1px solid ${colors.inputBorder}`,
                    backgroundColor: isPrimary ? '#2563eb' : isSuccess ? '#0f766e' : 'transparent',
                    color: isPrimary || isSuccess ? '#fff' : colors.text,
                    cursor: 'pointer',
                    fontWeight: 600,
                    fontSize: 13,
                  }}
                >
                  {action.label}
                </button>
              );
            })
          ) : (
            <>
              <button
                type="button"
                onClick={onClose}
                disabled={isSaving}
                style={{
                  height: '34px',
                  borderRadius: '8px',
                  padding: '0 12px',
                  border: `1px solid ${colors.inputBorder}`,
                  backgroundColor: 'transparent',
                  color: colors.text,
                  cursor: 'pointer',
                }}
              >
                {effectiveReadOnly ? 'Cerrar' : 'Cancelar'}
              </button>
              {!effectiveReadOnly ? (
                <button
                  type="button"
                  onClick={handleSaveClick}
                  disabled={isSaving}
                  style={{
                    height: '34px',
                    borderRadius: '8px',
                    padding: '0 12px',
                    border: 'none',
                    backgroundColor: '#2563eb',
                    color: '#fff',
                    cursor: 'pointer',
                  }}
                >
                  {isSaving ? 'Guardando...' : saveLabel || 'Guardar'}
                </button>
              ) : null}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default FormDrawer;
